import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { eur } from "@/lib/verticals";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Trash, Check, Clock, CalendarX, UsersThree } from "@phosphor-icons/react";

const VERTS = [["barbieri", "Barbieri"], ["beauty", "Beauty"], ["manutentori", "Manutentori"], ["ristorazione", "Ristorazione"]];
const DAYS = [["mon", "Lunedì"], ["tue", "Martedì"], ["wed", "Mercoledì"], ["thu", "Giovedì"], ["fri", "Venerdì"], ["sat", "Sabato"], ["sun", "Domenica"]];
const DEFAULT_HOURS = { mon: ["09:00", "18:00"], tue: ["09:00", "18:00"], wed: ["09:00", "18:00"], thu: ["09:00", "18:00"], fri: ["09:00", "18:00"], sat: ["09:00", "13:00"], sun: [] };

const ProviderOnboarding = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ business_name: "", vertical: "barbieri", bio: "", city: "", address: "" });
  const [services, setServices] = useState([]);
  const [svc, setSvc] = useState({ name: "", description: "", duration_min: 30, price: "" });
  const [hours, setHours] = useState(DEFAULT_HOURS);
  const [holidays, setHolidays] = useState([]);
  const [newHoliday, setNewHoliday] = useState("");
  const [staff, setStaff] = useState([]);
  const [newStaff, setNewStaff] = useState({ name: "", title: "" });
  const [saved, setSaved] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const loadExisting = useCallback(async () => {
    try {
      const r = await api.get("/providers/me");
      if (r.data) {
        setForm({ business_name: r.data.business_name, vertical: r.data.vertical, bio: r.data.bio || "", city: r.data.city, address: r.data.address || "" });
        setHours(r.data.working_hours || DEFAULT_HOURS);
        setHolidays(r.data.holidays || []);
        setSaved(true);
        const s = await api.get("/services");
        setServices(s.data);
        const st = await api.get("/staff");
        setStaff(st.data);
      }
    } catch {}
  }, []);

  useEffect(() => {
    if (!loading && !user) { navigate("/login"); return; }
    if (!loading && user && user.role !== "professionista") { navigate("/dashboard"); return; }
    if (user) loadExisting();
  }, [user, loading, navigate, loadExisting]);

  const saveProfile = async (e) => {
    e.preventDefault();
    try {
      const covers = {
        barbieri: "https://images.pexels.com/photos/13058812/pexels-photo-13058812.jpeg",
        beauty: "https://images.pexels.com/photos/33580448/pexels-photo-33580448.jpeg",
        manutentori: "https://images.pexels.com/photos/7859953/pexels-photo-7859953.jpeg",
        ristorazione: "https://images.pexels.com/photos/941861/pexels-photo-941861.jpeg",
      };
      await api.post("/providers", { ...form, working_hours: hours, holidays, cover_image: covers[form.vertical], avatar: user.picture || "https://images.pexels.com/photos/7065250/pexels-photo-7065250.jpeg" });
      setSaved(true);
      toast.success("Profilo salvato!");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const addService = async () => {
    if (!svc.name || !svc.price) { toast.error("Nome e prezzo obbligatori"); return; }
    try {
      const r = await api.post("/services", { ...svc, price: parseFloat(svc.price), duration_min: parseInt(svc.duration_min) });
      setServices((s) => [...s, r.data]);
      setSvc({ name: "", description: "", duration_min: 30, price: "" });
      toast.success("Servizio aggiunto");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const delService = async (id) => {
    await api.delete(`/services/${id}`);
    setServices((s) => s.filter((x) => x.service_id !== id));
  };

  const toggleDay = (day) => {
    setHours((h) => ({ ...h, [day]: h[day] && h[day].length ? [] : ["09:00", "18:00"] }));
  };
  const setDayTime = (day, idx, val) => {
    setHours((h) => { const arr = [...(h[day] || ["09:00", "18:00"])]; arr[idx] = val; return { ...h, [day]: arr }; });
  };
  const addHoliday = () => {
    if (newHoliday && !holidays.includes(newHoliday)) setHolidays((x) => [...x, newHoliday].sort());
    setNewHoliday("");
  };
  const addStaff = async () => {
    if (!newStaff.name) { toast.error("Inserisci il nome"); return; }
    try {
      const r = await api.post("/staff", newStaff);
      setStaff((s) => [...s, r.data]);
      setNewStaff({ name: "", title: "" });
      toast.success("Membro aggiunto");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const delStaff = async (id) => {
    await api.delete(`/staff/${id}`);
    setStaff((s) => s.filter((x) => x.staff_id !== id));
  };

  if (!user) return null;

  return (
    <div className="max-w-2xl mx-auto px-4 md:px-6 py-8">
      <h1 className="font-heading text-3xl font-bold">Onboarding professionista</h1>
      <p className="text-muted-foreground">Configura il tuo profilo e catalogo servizi.</p>

      <form onSubmit={saveProfile} className="mt-6 bg-card border border-border rounded-2xl p-5 md:p-6 space-y-4">
        <h2 className="font-heading font-semibold">1. Profilo attività</h2>
        <div><Label>Nome attività</Label><Input value={form.business_name} onChange={(e) => set("business_name", e.target.value)} required data-testid="pf-name" className="mt-1.5" /></div>
        <div>
          <Label>Categoria (verticale)</Label>
          <Select value={form.vertical} onValueChange={(v) => set("vertical", v)}>
            <SelectTrigger className="mt-1.5" data-testid="pf-vertical"><SelectValue /></SelectTrigger>
            <SelectContent>{VERTS.map(([id, l]) => <SelectItem key={id} value={id}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Città</Label><Input value={form.city} onChange={(e) => set("city", e.target.value)} required data-testid="pf-city" className="mt-1.5" /></div>
          <div><Label>Indirizzo</Label><Input value={form.address} onChange={(e) => set("address", e.target.value)} data-testid="pf-address" className="mt-1.5" /></div>
        </div>
        <div><Label>Descrizione</Label><Textarea value={form.bio} onChange={(e) => set("bio", e.target.value)} data-testid="pf-bio" className="mt-1.5" /></div>
        <Button type="submit" className="rounded-full" data-testid="pf-save">{saved ? <><Check className="mr-1" /> Aggiorna profilo</> : "Salva profilo"}</Button>
      </form>

      <div className={`mt-6 bg-card border border-border rounded-2xl p-5 md:p-6 ${!saved ? "opacity-50 pointer-events-none" : ""}`}>
        <h2 className="font-heading font-semibold mb-4">2. Catalogo servizi</h2>
        <div className="space-y-2 mb-4">
          {services.map((s) => (
            <div key={s.service_id} className="flex items-center justify-between p-3 rounded-xl border border-border" data-testid={`svc-item-${s.service_id}`}>
              <div><div className="font-medium text-sm">{s.name}</div><div className="text-xs text-muted-foreground">{s.duration_min} min · {eur(s.price)}</div></div>
              <Button size="icon" variant="ghost" className="text-destructive" data-testid={`del-svc-${s.service_id}`} onClick={() => delService(s.service_id)}><Trash /></Button>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Input placeholder="Nome servizio" value={svc.name} onChange={(e) => setSvc({ ...svc, name: e.target.value })} data-testid="svc-name" />
          <Input placeholder="Prezzo €" type="number" value={svc.price} onChange={(e) => setSvc({ ...svc, price: e.target.value })} data-testid="svc-price" />
          <Input placeholder="Durata (min)" type="number" value={svc.duration_min} onChange={(e) => setSvc({ ...svc, duration_min: e.target.value })} data-testid="svc-duration" />
          <Input placeholder="Descrizione" value={svc.description} onChange={(e) => setSvc({ ...svc, description: e.target.value })} data-testid="svc-desc" />
        </div>
        <Button className="rounded-full mt-3 gap-1" onClick={addService} data-testid="add-svc"><Plus weight="bold" /> Aggiungi servizio</Button>
      </div>

      <div className={`mt-6 bg-card border border-border rounded-2xl p-5 md:p-6 ${!saved ? "opacity-50 pointer-events-none" : ""}`}>
        <h2 className="font-heading font-semibold mb-1 flex items-center gap-2"><Clock size={18} weight="duotone" /> 3. Orari di apertura</h2>
        <p className="text-sm text-muted-foreground mb-4">Imposta gli orari per ogni giorno. Disattiva i giorni di chiusura.</p>
        <div className="space-y-2" data-testid="hours-editor">
          {DAYS.map(([key, label]) => {
            const open = hours[key] && hours[key].length >= 2;
            return (
              <div key={key} className="flex items-center gap-3 flex-wrap">
                <button type="button" onClick={() => toggleDay(key)} data-testid={`day-toggle-${key}`}
                  className={`w-28 text-left px-3 py-2 rounded-lg text-sm border transition-colors ${open ? "border-primary bg-accent text-accent-foreground" : "border-border text-muted-foreground"}`}>
                  {label}
                </button>
                {open ? (
                  <div className="flex items-center gap-2">
                    <Input type="time" value={hours[key][0]} onChange={(e) => setDayTime(key, 0, e.target.value)} data-testid={`day-open-${key}`} className="w-32" />
                    <span className="text-muted-foreground">–</span>
                    <Input type="time" value={hours[key][1]} onChange={(e) => setDayTime(key, 1, e.target.value)} data-testid={`day-close-${key}`} className="w-32" />
                  </div>
                ) : <span className="text-sm text-muted-foreground">Chiuso</span>}
              </div>
            );
          })}
        </div>

        <h2 className="font-heading font-semibold mt-6 mb-1 flex items-center gap-2"><CalendarX size={18} weight="duotone" /> Giorni di ferie / chiusure</h2>
        <p className="text-sm text-muted-foreground mb-3">Le date aggiunte non saranno prenotabili.</p>
        <div className="flex flex-wrap gap-2 mb-3" data-testid="holidays-list">
          {holidays.map((h) => (
            <span key={h} className="inline-flex items-center gap-1 bg-secondary rounded-full pl-3 pr-1 py-1 text-sm" data-testid={`holiday-${h}`}>
              {h}
              <button type="button" onClick={() => setHolidays((x) => x.filter((d) => d !== h))} className="w-5 h-5 grid place-items-center rounded-full hover:bg-background"><Trash size={12} /></button>
            </span>
          ))}
          {holidays.length === 0 && <span className="text-sm text-muted-foreground">Nessuna chiusura impostata</span>}
        </div>
        <div className="flex gap-2">
          <Input type="date" value={newHoliday} onChange={(e) => setNewHoliday(e.target.value)} data-testid="holiday-input" className="w-48" />
          <Button type="button" variant="outline" className="rounded-full gap-1" onClick={addHoliday} data-testid="add-holiday"><Plus weight="bold" /> Aggiungi</Button>
        </div>

        <Button className="rounded-full mt-5" onClick={saveProfile} data-testid="save-hours"><Check className="mr-1" /> Salva orari e ferie</Button>
      </div>

      <div className={`mt-6 bg-card border border-border rounded-2xl p-5 md:p-6 ${!saved ? "opacity-50 pointer-events-none" : ""}`}>
        <h2 className="font-heading font-semibold mb-1 flex items-center gap-2"><UsersThree size={18} weight="duotone" /> 4. Staff / Barbieri</h2>
        <p className="text-sm text-muted-foreground mb-4">Aggiungi i membri del team. I clienti potranno scegliere l'operatore in fase di prenotazione.</p>
        <div className="space-y-2 mb-4" data-testid="staff-list">
          {staff.map((m) => (
            <div key={m.staff_id} className="flex items-center justify-between p-3 rounded-xl border border-border" data-testid={`staff-item-${m.staff_id}`}>
              <div><div className="font-medium text-sm">{m.name}</div><div className="text-xs text-muted-foreground">{m.title || "Operatore"}</div></div>
              <Button size="icon" variant="ghost" className="text-destructive" data-testid={`del-staff-${m.staff_id}`} onClick={() => delStaff(m.staff_id)}><Trash /></Button>
            </div>
          ))}
          {staff.length === 0 && <span className="text-sm text-muted-foreground">Nessun membro. La prenotazione userà l'attività intera.</span>}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Input placeholder="Nome (es. Luca)" value={newStaff.name} onChange={(e) => setNewStaff({ ...newStaff, name: e.target.value })} data-testid="staff-name" />
          <Input placeholder="Ruolo (es. Barbiere)" value={newStaff.title} onChange={(e) => setNewStaff({ ...newStaff, title: e.target.value })} data-testid="staff-title" />
        </div>
        <Button className="rounded-full mt-3 gap-1" onClick={addStaff} data-testid="add-staff"><Plus weight="bold" /> Aggiungi membro</Button>
      </div>

      <Button className="w-full rounded-full mt-6" disabled={!saved || services.length === 0} data-testid="finish-onboarding" onClick={() => navigate("/pro")}>
        Vai alla dashboard
      </Button>
    </div>
  );
};

export default ProviderOnboarding;
